import { EntityState } from './entity-state';
import type {
    EntitySearchArg,
    PageParam,
    PageResult,
    PagedMetaEnvelope,
    PagedQueryArg,
} from './paged-types';
import { SEARCH_RESULT_LIMIT } from './paged-types';
import { buildPagedQueryString, buildSearchWhere } from './query-params';

/** Columns the support package search matches against. */
export const SUPPORT_PACKAGE_SEARCH_COLUMNS = ['message', 'url'] as const;
const SUPPORT_PACKAGE_UUID_COLUMNS = ['id'] as const;

export type SupportPackageItem = {
    id: string;
    message: string;
    url: string;
    state: string;
    userId: string;
    subscriptionId: string;
    createdAt: string;
};

type SupportPackageApiItem = {
    id?: string;
    message?: string | null;
    url?: string | null;
    state?: string | null;
    userId?: string | null;
    subscriptionId?: string | null;
    createdAt?: string | null;
};

type SupportPackagesApiResponse = {
    supportPackages?: SupportPackageApiItem[];
    meta?: PagedMetaEnvelope;
};

type SupportPackageApiItemWithId = SupportPackageApiItem & { id: string };

const hasId = (pkg: SupportPackageApiItem): pkg is SupportPackageApiItemWithId =>
    typeof pkg.id === 'string';

const transformSupportPackage = (pkg: SupportPackageApiItemWithId): SupportPackageItem => ({
    id: pkg.id,
    message: pkg.message ?? '',
    url: pkg.url ?? '',
    state: pkg.state ?? EntityState.ACTIVE,
    userId: pkg.userId ?? '',
    subscriptionId: pkg.subscriptionId ?? '',
    createdAt: pkg.createdAt ?? '',
});

export type SupportPackagesPageResult = PageResult<SupportPackageItem>;
export type SupportPackagesQueryArg = PagedQueryArg;

/**
 * Lightweight, non-paginated search used by the global TopBar typeahead.
 * Returns at most `limit` matching support packages, newest first.
 */
export const searchSupportPackagesQuery = {
    query: ({ term, limit = SEARCH_RESULT_LIMIT + 1, where }: EntitySearchArg) => ({
        url: `/mp/1/supportPackages?${buildPagedQueryString({
            offset: 0,
            limit,
            order: [['createdAt', 'DESC']],
            where: {
                ...buildSearchWhere(
                    SUPPORT_PACKAGE_SEARCH_COLUMNS,
                    term,
                    SUPPORT_PACKAGE_UUID_COLUMNS,
                ),
                ...where,
            },
        })}`,
        method: 'get',
    }),
    transformResponse: (response: SupportPackagesApiResponse): SupportPackageItem[] =>
        (response?.supportPackages ?? []).filter(hasId).map(transformSupportPackage),
};

export const supportPackagesInfiniteQuery = {
    query: ({
        queryArg,
        pageParam,
    }: {
        queryArg: SupportPackagesQueryArg;
        pageParam: PageParam;
    }) => ({
        url: `/mp/1/supportPackages?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: SupportPackagesApiResponse): SupportPackagesPageResult => {
        const rows = (response?.supportPackages ?? []).filter(hasId).map(transformSupportPackage);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    keepUnusedDataFor: 300,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: SupportPackagesPageResult,
            _allPages: SupportPackagesPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
};
